export type ColumnId = 'todo' | 'doing' | 'review' | 'done'

export interface Person {
  id: string
  name: string
  initial: string
  color: string
}

export interface TicketProject {
  id: string
  name: string
  badgeClass: string
  dotClass: string
  managerIds: string[]
  developerIds: string[]
  visibility: 'interne' | 'externe'
}

export interface Ticket {
  id: string
  projectId: string
  title: string
  description: string
  assigneeIds: string[]
  cpIds: string[]
  dueDate: string
  status: ColumnId
  order: number
  doneAt: string | null
  reviewedAt: string | null
  timeSpentDays: number | null
  timeSpentUnit: 'days' | 'hours' | null
  cpTimeSpentDays: number | null
  cpTimeSpentUnit: 'days' | 'hours' | null
}

const PALETTE = [
  { badge: 'bg-violet-100 text-violet-700', dot: 'bg-violet-500', color: '#a78bfa' },
  { badge: 'bg-amber-100 text-amber-700', dot: 'bg-amber-500', color: '#fbbf24' },
  { badge: 'bg-teal-100 text-teal-700', dot: 'bg-teal-500', color: '#2dd4bf' },
  { badge: 'bg-rose-100 text-rose-600', dot: 'bg-rose-500', color: '#fb7185' },
  { badge: 'bg-sky-100 text-sky-700', dot: 'bg-sky-500', color: '#38bdf8' },
  { badge: 'bg-fuchsia-100 text-fuchsia-700', dot: 'bg-fuchsia-500', color: '#e879f9' },
]

function paletteIndex(id: string): number {
  let hash = 0
  for (let index = 0; index < id.length; index += 1) {
    hash = (hash * 31 + id.charCodeAt(index)) >>> 0
  }
  return hash % PALETTE.length
}

export function personFromName(id: string, name: string): Person {
  const trimmed = name.trim()
  return {
    id,
    name: trimmed || 'Sans nom',
    initial: (trimmed.charAt(0) || '?').toUpperCase(),
    color: PALETTE[paletteIndex(id)].color,
  }
}

export function projectStyle(id: string): { badgeClass: string; dotClass: string } {
  const tone = PALETTE[paletteIndex(id)]
  return { badgeClass: tone.badge, dotClass: tone.dot }
}

function toISODate(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export const MONTHS_SHORT = [
  'janv.',
  'févr.',
  'mars',
  'avr.',
  'mai',
  'juin',
  'juil.',
  'août',
  'sept.',
  'oct.',
  'nov.',
  'déc.',
]

export function formatDueDate(iso: string): string {
  const [, month, day] = iso.split('-').map(Number)
  return `${String(day).padStart(2, '0')} ${MONTHS_SHORT[month - 1]}`
}

export function formatDaysPassed(iso: string): string {
  const [year, month, day] = iso.split('-').map(Number)
  const then = new Date(year, month - 1, day)
  const now = new Date()
  now.setHours(0, 0, 0, 0)
  then.setHours(0, 0, 0, 0)
  const diff = Math.round((now.getTime() - then.getTime()) / 86_400_000)
  if (diff <= 0) return "aujourd'hui"
  if (diff === 1) return '1j passé'
  return `${diff}j passés`
}

export function formatTimeSpent(days: number, unit?: 'days' | 'hours' | null): string {
  const amount = unit === 'hours' ? Math.round(days * 8 * 1000) / 1000 : Math.round(days * 1000) / 1000
  const label = Number.isInteger(amount) ? String(amount) : String(amount).replace('.', ',')
  return unit === 'hours' ? `${label} h` : `${label} j`
}

export function todayISO(): string {
  return toISODate(new Date())
}

function parseISODate(value: string): Date {
  const [year, month, day] = value.slice(0, 10).split('-').map(Number)
  return new Date(year, (month || 1) - 1, day || 1)
}

function startOfLocalToday(now = new Date()): Date {
  return new Date(now.getFullYear(), now.getMonth(), now.getDate())
}

/**
 * Retard d'un ticket.
 * À faire / En cours : comparaison avec aujourd'hui.
 * En révision ou Fait : le retard est figé au jour où le développeur a clos
 * (premier passage en révision, sinon done_at). Il ne grossit plus.
 * S'il a été clos dans les temps, il n'est plus en retard, même si l'échéance
 * est depuis dépassée. S'il était en retard, le +Xj reste affiché.
 */
export function ticketDelay(
  ticket: {
    status: string
    dueDate: string
    doneAt?: string | null
    reviewedAt?: string | null
  },
  today = startOfLocalToday(),
): { text: string; late: boolean } {
  const due = parseISODate(ticket.dueDate)
  let reference: Date | null
  if (ticket.status === 'review' || ticket.status === 'done') {
    const closedAt = ticket.reviewedAt || (ticket.status === 'done' ? ticket.doneAt : null)
    // Sans date de clôture connue, un ticket en révision garde le retard courant.
    reference = closedAt ? parseISODate(closedAt) : ticket.status === 'review' ? today : null
  } else {
    reference = today
  }
  if (!reference) return { text: '—', late: false }
  const lateDays = Math.round((reference.getTime() - due.getTime()) / 86_400_000)
  if (lateDays <= 0) return { text: '—', late: false }
  return { text: `+${lateDays}j`, late: true }
}
